import { schema } from 'normalizr';
import { Observable } from 'rxjs';
import { fromPromise } from 'rxjs/observable/fromPromise';
import { of } from 'rxjs/observable/of';
import { FileSystemHelper } from '../../../app/helpers/file-system.helper';
import { FileHelper, PathInfoType } from '../../../app/helpers/file-system/file.helper';
import { isInArray } from '../../../core/util/is-in-array';
import { VasAppFacade } from '../../services/vas-app-facade.service';
import { JobDataFacade } from '../job-data/job-data.facade';
import { FileCordovaFacade } from './file-cordova.facade';
import { FileDesktopFacade } from './file-desktop.facade';
import { FileApiFacade } from './file.api-facade';
import { File, File as FileModel } from './file.model';
import { FileStoreFacade } from './file.store-facade';

export class FileFacade extends JobDataFacade<File, FileApiFacade, FileStoreFacade> {

    protected fileCordovaFacade: FileCordovaFacade;
    protected fileDesktopFacade: FileDesktopFacade;

    constructor(
        protected entityName: string,
        protected appFacade: VasAppFacade,
        protected apiFacade: FileApiFacade,
        protected storeFacade: FileStoreFacade,
        protected entitySchema: schema.Entity
    ) {
        super(entityName, appFacade, apiFacade, storeFacade, entitySchema);

        this.fileCordovaFacade = new FileCordovaFacade(appFacade);
        this.fileDesktopFacade = new FileDesktopFacade(appFacade);
    }

    get cordova(): FileCordovaFacade {
        return this.fileCordovaFacade;
    }

    get desktop(): FileDesktopFacade {
        return this.fileDesktopFacade;
    }

    /**
     * This method is a wrapper for _getPreviewUrl. Its purpose is to allow us
     * to invoke getPreviewUrl multiple times for the same file id without
     * triggering multiple requests to the api to download it. Which can happen
     * when the caller is in a list that is being rebuilt over and over again.
     *
     * @param file {File}
     * @return {Observable<string>}
     */
    getPreviewUrl(file: File): Observable<string> {

        return fromPromise(this._getPreviewUrl(file));
    }

    protected async _getPreviewUrl(file: File): Promise<string> {
        const mimeType = file.mimeType || '';

        switch (true) {
            case !!file.imageDataUrl : {
                return file.imageDataUrl;
            }
            case mimeType === 'text/html': {
                const url = await this.fileToDataUrl(file);
                return url.replace('text/html', 'image/svg+xml');
            }
            case !!(mimeType || '').match(/image\//): {
                return this.getThumbnail(file);
            }
            case !!(mimeType || '').match(/video\//): {
                return Promise.resolve('assets/content-types/video-placeholder.png');
            }
            case !!(mimeType || '').match(/audio\//): {
                return Promise.resolve('assets/content-types/mp3.svg');
            }
            case mimeType === 'application/zip': {
                return Promise.resolve('assets/content-types/zip.svg');
            }
            case mimeType === 'application/pdf': {
                return Promise.resolve('assets/content-types/pdf.svg');
            }
            case isInArray(mimeType, [
                'text/plain',
                'application/sql'
            ]): {
                return Promise.resolve('assets/content-types/txt.svg');
            }
            case isInArray(mimeType, [
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'application/vnd.ms-excel']
            ) : {
                return Promise.resolve('assets/content-types/xls.svg');
            }
            case isInArray(mimeType, [
                'application/msword',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
                'application/vnd.ms-word.document.macroEnabled.12'
            ]): {
                return Promise.resolve('assets/content-types/doc.svg');
            }
            default: {
                return Promise.resolve('assets/content-types/file.svg');
            }
        }
    }

    fileExistsLocally(file: FileModel): Observable<boolean> {

        if (!file.localFileSystemPath) {
            return of(false);
        }

        const parsed = FileHelper.parsePath(file.localFileSystemPath);

        return parsed.cordova ?
            this.cordova.fileExists(file) :
            this.desktop.fileExists(file);
    }

    /**
     * This method will return the contents of the file as a blob.
     *
     * The method assumes that the file has either just been uploaded to the dom
     * or that the contents was already downloaded to the app's local cache on
     * the device's persistent memory, in either case the method will reject if
     * the value in localFileSystemPath is not found, invalid or expired.
     *
     * @param file {File}
     */
    async fileToBlob(file: File): Promise<Blob> {
        const localFileSystemPath = file.localFileSystemPath;

        if (!localFileSystemPath) {
            return this.api.getFileContent(file).toPromise();
        }

        const pathInfo = FileHelper.parsePath(localFileSystemPath);

        switch (true) {
            case pathInfo.type === PathInfoType.BLOB: {
                return FileSystemHelper.objectUrlToBlob(localFileSystemPath);
            }
            case pathInfo.cordova: {
                return this.cordova.fileToBlob(file);
            }
            case !pathInfo.cordova: {
                return this.desktop.fileToBlob(file);
            }
            default: {
                return Promise.reject(Error('unable to convert file to blob'));
            }
        }
    }

    fileToDataUrl(file: File): Promise<string> {

        const localFileSystemPath = file.localFileSystemPath;

        if (!localFileSystemPath) {
            return this.api.getFileContent(file)
                .concatMap(blob => FileSystemHelper.blobToDataUrl(blob))
                .toPromise();
        }

        const pathInfo = FileHelper.parsePath(localFileSystemPath);

        return pathInfo.cordova ?
            this.cordova.fileToDataUrl(file) :
            this.desktop.fileToDataUrl(file);
    }

    getThumbnail(
        file: File,
        dims: {width: number, height: number} = {width: 400, height: undefined}
    ): Promise<string> {

        const localFileSystemPath = file.localFileSystemPath;

        if (!localFileSystemPath) {
            return this.appFacade.remoteThumbnailService
                .getThumbnailUrl(file.account, file.id, dims)
                .toPromise();
        }

        const pathInfo = FileHelper.parsePath(localFileSystemPath);

        if (pathInfo.cordova) {
            return this.appFacade.cordovaThumbnailService.getThumbnail(
                file.localFileSystemPath,
                file.mimeType,
                {
                    maxPixelSize: 300
                }
            );
        }
        return this.desktop.fileToDataUrl(file);
    }
}



// WEBPACK FOOTER //
// ./src/vas/business-objects/file/file.facade.ts